import type { QueryResult } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import _ from 'lodash';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useIsLoggedIn from '../account/useIsLoggedIn';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { useAllUsersMap } from '../user/useAllUsers';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import populateObjects from '../utils/populateObjects';
import useIsPublicView from '../utils/useIsPublicView';

gql(`
  # eslint-disable-next-line slab-graphql/no-unused-fields
  fragment AllGroupFields on Group {
    id
    name
    description
    banner {
      original
    }
    allUsers {
      id
    }
    children {
      id
    }
  }
`);

const ALL_GROUPS_QUERY = gql(`
  query allGroups {
    session: currentSession {
      organization {
        id
        groups {
          id
          ...AllGroupFields
        }
      }
    }
  }
`);

const GROUP_CREATED_SUBSCRIPTION = gql(`
  subscription groupCreated {
    group: groupCreated {
      ...AllGroupFields
    }
  }
`);

const GROUP_DELETED_SUBSCRIPTION = gql(`
  subscription groupDeleted {
    group: groupDeleted {
      id
    }
  }
`);

const GROUP_UPDATED_SUBSCRIPTION = gql(`
  subscription groupUpdated {
    group: groupUpdated {
      ...AllGroupFields
    }
  }
`);

const useSubscribeEffect = (
  subscribeToMore: QueryResult<
    DocumentType<typeof ALL_GROUPS_QUERY>,
    VariablesOf<typeof ALL_GROUPS_QUERY>
  >['subscribeToMore'],
  skip: boolean,
) => {
  useEffect(() => {
    if (skip) return undefined;

    const unsubscribes = [
      GROUP_CREATED_SUBSCRIPTION,
      GROUP_DELETED_SUBSCRIPTION,
      GROUP_UPDATED_SUBSCRIPTION,
    ].map((query) => {
      const path = 'session.organization.groups';
      return subscribeToMore({
        document: query,
        updateQuery: updateSubscribedList(
          path,
          'group',
          query !== GROUP_DELETED_SUBSCRIPTION,
        ),
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [skip, subscribeToMore]);
};

const populateGroupUsers = <
  User,
  Group extends { allUsers?: { id: string }[] },
>(
  groups: Group[],
  userMap: { [id: string]: User },
) =>
  groups.map((group) => ({
    ...group,
    allUsers: group.allUsers && populateObjects(userMap, group.allUsers),
  }));

type UseAllGroupQueryOptions = {
  skip?: boolean;
};

const useAllGroupsQuery = (options?: UseAllGroupQueryOptions) => {
  const userMap = useAllUsersMap(options);
  const response = useQuery(ALL_GROUPS_QUERY, options);

  useSubscribeEffect(response.subscribeToMore, options?.skip ?? false);

  return useMemo(
    () =>
      populateGroupUsers(
        response?.data?.session.organization.groups ?? [],
        userMap,
      ),
    [response, userMap],
  );
};

type UseAllGroupOptions = {
  skip?: boolean;
};

const AllGroupsContext = createContext<{
  groups: ReturnType<typeof useAllGroupsQuery>;
  groupMap: _.Dictionary<ReturnType<typeof useAllGroupsQuery>[number]>;
  query: () => void;
}>({ groups: [], groupMap: {}, query: () => {} });

const useAllGroups = (options?: UseAllGroupOptions) => {
  const { groups, query } = useContext(AllGroupsContext);

  useEffect(() => {
    if (!options?.skip) query();
  }, [options?.skip, query]);

  return groups;
};

export const useAllGroupMap = (options?: UseAllGroupOptions) => {
  const { groupMap, query } = useContext(AllGroupsContext);

  useEffect(() => {
    if (!options?.skip) query();
  }, [options?.skip, query]);

  return groupMap;
};

interface AllGroupsProviderProps {
  children: React.ReactNode;
}

export const AllGroupsProvider = ({ children }: AllGroupsProviderProps) => {
  const [isIdle, setIsIdle] = useState(true);

  const groups = useAllGroupsQuery({ skip: isIdle });

  const isLoggedIn = useIsLoggedIn();
  const isPublicView = useIsPublicView();

  const value = useMemo(
    () => ({
      query: () => setIsIdle(!isLoggedIn || isPublicView),
      groups,
      groupMap: _.keyBy(groups, 'id'),
    }),
    [isLoggedIn, isPublicView, groups],
  );

  return (
    <AllGroupsContext.Provider value={value}>
      {children}
    </AllGroupsContext.Provider>
  );
};

export default useAllGroups;
